import React from 'react';
import * as Sentry from '@sentry/react';

import { openCrashReportFromUtils } from './utils/crashReportUtils';
import { ErrorLoggerFactory } from './hooks/useGlobalErrorLogger';
import { serializeError, ERROR_LEVELS, classifyErrorByType } from './utils/errors/errorsUtils';
import { getIds } from './utils/hooks';

class CriticalErrorBoundary extends React.Component {
  isToolkitEnabled = process.env.REACT_APP_TOOLKIT?.toUpperCase() === 'TRUE';
  isDevOrStageEnv = process.env.NODE_ENV === 'development' || this.isToolkitEnabled;

  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // If we reach here, it's automatically critical because React intercepted
    // an error that would have crashed the rendering

    const { userId, guestId } = getIds();
    const cartData = localStorage.getItem('cartItems');
    const cart = cartData && cartData !== 'undefined' ? JSON.parse(cartData) : null;
    const serializableError = serializeError(error);
    const { errorType } = classifyErrorByType(error);

    console.error('🚨 Critical React Error intercepted:', error);
    console.error('📍 Error Info:', errorInfo);

    // Log to Backend with CRITIAL flag (isTriggerCrashReport = true)
    ErrorLoggerFactory.logError(
      cart?._id,
      serializableError,
      errorType,
      { userId, guestId, criticality: ERROR_LEVELS.CRITICAL },
      true
    );

    Sentry.withScope((scope) => {
      const storeId = JSON.parse(localStorage.getItem('store'))?.store?.id

      scope.setTag('errorBoundary', true);
      scope.setTag('critical', true);
      if (storeId) scope.setTag('storeId', storeId);

      scope.setLevel('fatal');

      scope.setContext('errorBoundary', {
        componentStack: errorInfo.componentStack,
        errorBoundaryName: 'CriticalErrorBoundary'
      });

      scope.addBreadcrumb({
        message: 'React Error Boundary triggered',
        level: 'fatal',
        data: {
          errorMessage: error.message,
          componentStack: errorInfo.componentStack
        }
      });

      const eventId = Sentry.captureException(error);

      this.setState({ eventId });

      setTimeout(() => {
        openCrashReportFromUtils({
          error,
          eventId
        });
      }, this.isDevOrStageEnv ? 3000 : 250);
    });
  }

  render() {
    if (this.state.hasError) {
      if (!this.isDevOrStageEnv) return (<></>)

      return (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          backgroundColor: '#f8f9fa',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          fontFamily: 'Arial, sans-serif'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            maxWidth: '500px'
          }}>
            <h2 style={{ color: '#dc3545', marginBottom: '20px' }}>
              ⚠️ Critical error detected
            </h2>

            <p style={{ color: '#666', marginBottom: '20px' }}>
              An unexpected error prevented this part of the application from displaying.
            </p>

            {this.state.eventId && (
              <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
                Error ID: <code>{this.state.eventId}</code>
              </p>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => window.location.reload()}
                style={{
                  padding: '8px 24px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                🔄 Reload page
              </button>

              <button
                type="button"
                onClick={() => window.history.back()}
                style={{
                  padding: '8px 24px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                ← Go back
              </button>
            </div>

            <pre style={{
              backgroundColor: '#f8f9fa',
              padding: '10px',
              borderRadius: '4px',
              overflow: 'auto',
              fontSize: '12px',
              marginTop: '10px'
            }}>
              {this.state?.error?.toString()}
              {this.state?.errorInfo?.componentStack}
            </pre>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export { CriticalErrorBoundary };